import { useEffect, useState } from 'react';
import api from '@/lib/api';

/**
 * Phase 2I — Live reactions dock.
 * Floats over a game / tournament match. Click an emoji → it pulses into the
 * realtime channel, every viewer sees it animate. Pure transient social energy.
 */
const REACTIONS = ['🔥', '👏', '⚽', '🤝', '🎯', '🏆', '💪'];

export default function ArenaxReactionDock({ targetType, targetId, incomingReaction }) {
  // Incoming reactions float up; we display a small queue of recent ones.
  const [floats, setFloats] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!incomingReaction) return;
    const id = Math.random().toString(36).slice(2);
    const item = { ...incomingReaction, id };
    setFloats(prev => [...prev, item]);
    // Auto-remove after 3.5s
    const t = setTimeout(() => setFloats(prev => prev.filter(f => f.id !== id)), 3500);
    return () => clearTimeout(t);
  }, [incomingReaction]);

  const drop = async (emoji) => {
    if (busy) return;
    setBusy(true);
    // Optimistic float
    const id = Math.random().toString(36).slice(2);
    setFloats(prev => [...prev, { id, emoji, user_name: 'You' }]);
    setTimeout(() => setFloats(prev => prev.filter(f => f.id !== id)), 3500);
    try {
      await api.post('/arenax/reactions', { emoji, target_type: targetType, target_id: targetId });
    } catch {}
    finally { setBusy(false); }
  };

  return (
    <div className="relative" data-testid="arenax-reaction-dock">
      {/* Floating animation lane */}
      <div className="absolute -top-16 left-0 right-0 h-16 overflow-hidden pointer-events-none">
        {floats.map(f => (
          <div key={f.id}
            data-testid={`arenax-reaction-float-${f.emoji}`}
            className="absolute bottom-0 text-3xl select-none"
            style={{
              left: `${20 + Math.floor(Math.random() * 60)}%`,
              animation: 'arenax-reaction-float 3.5s ease-out forwards',
            }}>
            {f.emoji}
          </div>
        ))}
      </div>
      <style>{`
        @keyframes arenax-reaction-float {
          0%   { transform: translateY(0px) scale(0.8); opacity: 0; }
          15%  { transform: translateY(-8px) scale(1.05); opacity: 1; }
          80%  { transform: translateY(-50px) scale(1); opacity: 0.6; }
          100% { transform: translateY(-70px) scale(0.85); opacity: 0; }
        }
      `}</style>
      {/* Dock */}
      <div className="inline-flex items-center gap-1 px-2 py-1.5 rounded-full border border-slate-700 bg-slate-900/80 backdrop-blur">
        {REACTIONS.map(e => (
          <button key={e} onClick={() => drop(e)} disabled={busy}
            data-testid={`arenax-reaction-btn-${e}`}
            className="text-lg w-8 h-8 rounded-full hover:bg-slate-800 hover:scale-110 transition-all disabled:opacity-40">
            {e}
          </button>
        ))}
      </div>
    </div>
  );
}
